import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ListTree, Plus, Pencil, X, Save, RefreshCw, GripVertical, ChevronRight, ChevronDown, Eye, EyeOff, ChevronsDown, ChevronsUp } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { navigationAdminApi } from '../../services/api';
import { applySearch } from '../../utils/listSearch';
import { iconFor, ICON_NAMES } from '../../utils/navIcons';
import StuiSelect from '../../components/common/StuiSelect';
import SearchSelect from '../../components/common/SearchSelect';
import SearchClearIcon from '../../components/common/SearchClearIcon';

/**
 * Development > Configuration > Navigation — the editor for AppCfg_Navigation, the table the sidebar,
 * every page guard and every write's X-Nav-Key come from. SysAdmin only (the API is [RequireSysAdmin]).
 *
 * Rows are added and changed here; a row is never deleted, only hidden, so its key is never reused.
 * The server checks every save (NavRowRules). A change that reaches past the row — hiding a page
 * that writes, a new key, a report moving section, a row the code names, anything that changes what
 * the row is (type, target, slug, write surface, bulk delete), a new Page row — comes back with its
 * consequences in words, and saves only when the person confirms them.
 *
 * Edit mode as Task Log has it: the pencil turns it on, a double-click then opens a row to edit, Save
 * ends edit mode and Cancel stays in it. Outside edit mode a double-click shows the row read-only.
 * Who can open and save from each row is the access matrix's (Security > Permissions > Matrix).
 */

const SEARCH_FIELDS = ['label', 'navKey', 'slug', 'targetKey', 'permissionKey', 'navType'];
const EMPTY = {
  navKey: '', parentKey: '', navType: 'Page', label: '', sortOrder: 0, slug: '', targetKey: '', permissionKey: '',
  sysAdminOnly: false, isFuture: false, isHidden: false, searchable: true, surfaceType: '', allowBulkDelete: false, icon: '',
};
const FLAGS = [
  ['sysAdminOnly', 'SysAdmin only'],
  ['isFuture', 'Future'],
  ['isHidden', 'Hidden'],
  ['searchable', 'Searchable'],
  ['allowBulkDelete', 'Allow bulk delete'],
];

// Which fields a type uses. The others are left out of the popup; their stored values are sent back unchanged.
const FIELDS_BY_TYPE = {
  Header: ['targetKey', 'slug', 'permissionKey', 'icon'],
  Engineering: ['targetKey', 'slug', 'permissionKey', 'surfaceType', 'icon'],
  DataEntry: ['targetKey', 'slug', 'permissionKey', 'surfaceType', 'icon'],
  Report: ['targetKey', 'slug'],
  Page: ['targetKey', 'slug', 'permissionKey', 'surfaceType', 'icon'],
};
const uses = (type, field) => (FIELDS_BY_TYPE[type] || FIELDS_BY_TYPE.Page).includes(field);

const KEY_HINT = {
  Header: 'Who sees the group. None: it shows when something under it opens.',
  Engineering: 'Who can open the page. None: anyone who can read its table (the table owner key).',
  DataEntry: 'Who can open the module and save from it. None: SysAdmins only.',
  Page: 'Who can open the page (and save, if it writes). None: SysAdmins only.',
};
const TARGET_HINT = {
  Engineering: 'The table the page lists and edits. Only tables with an owner are offered.',
  DataEntry: 'The data entry module the page opens.',
  Report: 'The report the row opens.',
  Page: 'The page key the code reads, usually the route path. Leave empty if the code needs none.',
  Header: 'The dashboard page the group opens, if it has one.',
};

// SearchSelect shows displayValue, not the option's text, so each picker passes it.
const shown = (options, value) => options.find(o => String(o.id) === String(value))?.display;

const sortRows = (a, b) => (a.sortOrder - b.sortOrder) || a.navKey.localeCompare(b.navKey);

const NavigationEditorPage = () => {
  const { notify } = useNotifications();
  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editing, setEditing] = useState(null);           // { row, isNew, readOnly }
  const [consequences, setConsequences] = useState(null); // strings from a 409
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([navigationAdminApi.rows(), navigationAdminApi.targets()]);
      setRows(r.data || []);
      setTargets(t.data);
      setExpanded(prev => (prev.size ? prev : new Set((r.data || []).filter(x => !x.parentKey).map(x => x.navKey))));
    } catch (err) {
      notify.error(err.response?.data?.error || 'Could not load the navigation rows');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const byKey = useMemo(() => new Map(rows.map(r => [r.navKey, r])), [rows]);
  const childrenOf = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const p = r.parentKey && byKey.has(r.parentKey) ? r.parentKey : '';
      if (!m.has(p)) m.set(p, []);
      m.get(p).push(r);
    });
    m.forEach(list => list.sort(sortRows));
    return m;
  }, [rows, byKey]);

  const pathOf = useCallback((key) => {
    const parts = [];
    for (let r = byKey.get(key); r; r = r.parentKey ? byKey.get(r.parentKey) : null) parts.unshift(r.label);
    return parts.join(' > ');
  }, [byKey]);

  // Searching shows each match with the rows above it, opened.
  const matchKeys = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const keep = new Set();
    applySearch(rows, searchTerm, SEARCH_FIELDS).forEach(r => {
      for (let x = r; x; x = x.parentKey ? byKey.get(x.parentKey) : null) keep.add(x.navKey);
    });
    return keep;
  }, [rows, searchTerm, byKey]);

  const visible = useMemo(() => {
    const out = [];
    const walk = (parent, depth) => (childrenOf.get(parent) || []).forEach(r => {
      if (r.isHidden && !showHidden) return;
      if (matchKeys && !matchKeys.has(r.navKey)) return;
      const kids = (childrenOf.get(r.navKey) || []).filter(k => showHidden || !k.isHidden);
      out.push({ row: r, depth, hasKids: kids.length > 0 });
      if (matchKeys || expanded.has(r.navKey)) walk(r.navKey, depth + 1);
    });
    walk('', 0);
    return out;
  }, [childrenOf, expanded, showHidden, matchKeys]);

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const selected = selectedKey ? byKey.get(selectedKey) : null;

  const startAdd = () => {
    const parent = selected && selected.navType === 'Header' ? selected.navKey : selected?.parentKey || '';
    const siblings = childrenOf.get(parent) || [];
    setConsequences(null);
    setEditMode(true);
    setEditing({ isNew: true, row: { ...EMPTY, parentKey: parent, sortOrder: siblings.length ? Math.max(...siblings.map(s => s.sortOrder)) + 10 : 10 } });
  };

  const openRow = (row, readOnly) => {
    if (!row) return;
    setConsequences(null);
    const r = {};
    Object.keys(EMPTY).forEach(k => { r[k] = row[k] ?? EMPTY[k]; });
    setEditing({ isNew: false, readOnly, row: r });
  };

  const original = useMemo(() => {
    if (!editing || editing.isNew) return null;
    const src = byKey.get(editing.row.navKey);
    const r = {};
    Object.keys(EMPTY).forEach(k => { r[k] = src?.[k] ?? EMPTY[k]; });
    return r;
  }, [editing, byKey]);

  const dirty = !!editing && (editing.isNew || Object.keys(EMPTY).some(k => editing.row[k] !== original?.[k]));

  const setField = (field, value) => {
    setConsequences(null);
    setEditing(prev => {
      const row = { ...prev.row, [field]: value };
      // A picked target suggests its URL when none is set yet.
      if (field === 'targetKey' && value && !prev.row.slug) {
        if (row.navType === 'Report') row.slug = `reports/${value}`;
        else if (row.navType === 'Engineering' || row.navType === 'DataEntry') row.slug = row.navKey || String(value).toLowerCase().replace(/_/g, '-');
      }
      if (field === 'navType' && value === 'Report') row.permissionKey = '';
      return { ...prev, row };
    });
  };

  const cancel = () => {
    if (editing.readOnly) { setEditing(null); return; }
    if (dirty && !editing.isNew) {
      notify.confirm('Discard unsaved changes?', () => { setEditing(null); setConsequences(null); });
      return;
    }
    setEditing(null);
    setConsequences(null);
  };

  const save = async (confirm = false) => {
    if (!editing) return;
    const r = editing.row;
    const body = {
      ...r,
      sortOrder: Number(r.sortOrder) || 0,
      parentKey: r.parentKey || null, slug: r.slug || null, targetKey: r.targetKey || null,
      permissionKey: r.permissionKey || null, surfaceType: r.surfaceType || null, icon: r.icon || null,
      confirm,
    };
    setSaving(true);
    try {
      if (editing.isNew) await navigationAdminApi.create(body);
      else await navigationAdminApi.update(r.navKey, body);
      setEditing(null);
      setEditMode(false);
      setConsequences(null);
      setSelectedKey(r.navKey);
      if (r.parentKey) setExpanded(prev => new Set(prev).add(r.parentKey));
      const res = await navigationAdminApi.rows();
      setRows(res.data || []);
      notify.success(`Saved '${r.label}'. Other API instances pick it up within five minutes.`);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.consequences) setConsequences(err.response.data.consequences);
      else notify.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const hiddenCount = rows.filter(r => r.isHidden).length;

  const parentOptions = useMemo(() => rows
    .filter(r => !editing || r.navKey !== editing.row.navKey)
    .filter(r => r.navType === 'Header' || r.navType === 'Page' || r.navType === 'Engineering')
    .map(r => ({ id: r.navKey, display: `${pathOf(r.navKey)}${r.isHidden ? ' (hidden)' : ''}` }))
    .sort((a, b) => a.display.localeCompare(b.display)), [rows, editing, pathOf]);

  const targetField = () => {
    const r = editing.row;
    const pick = (options, placeholder) => (
      <SearchSelect className="stui-field-input" value={r.targetKey} options={options} placeholder={placeholder} disabled={editing.readOnly}
        displayValue={shown(options, r.targetKey) || r.targetKey} onChange={v => setField('targetKey', v || '')} />
    );
    switch (r.navType) {
      case 'Engineering':
        return pick((targets?.tables || []).map(t => ({ id: t.table, display: `${t.table} (${t.ownerKey})` })), '-- Owned table --');
      case 'DataEntry':
        return pick((targets?.modules || []).map(m => ({ id: m.slug, display: `${m.name} (${m.slug})` })), '-- Data entry module --');
      case 'Report':
        return pick((targets?.reports || []).map(x => ({ id: x.slug, display: `${x.name}${x.placed ? '' : ' (not in navigation)'}` })), '-- Report --');
      default:
        return <input className="stui-field-input" value={r.targetKey} disabled={editing.readOnly} onChange={e => setField('targetKey', e.target.value)}
          placeholder={r.navType === 'Page' ? 'page key (usually the route path)' : 'dashboard page, if any'} />;
    }
  };

  const permissionOptions = useMemo(() => (targets?.permissions || []).map(p => ({ id: p.key, display: `${p.key} — ${p.name}` })), [targets]);

  const PickedIcon = editing?.row.icon ? iconFor(editing.row.icon) : null;
  const ro = !!editing?.readOnly;

  if (loading && !rows.length) return <div className="stui-detail-empty"><ListTree size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <ListTree size={20} />
          <h1>Navigation</h1>
          <span className="stui-module-header-meta">{rows.length} rows{hiddenCount ? ` · ${hiddenCount} hidden` : ''}</span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-search-wrapper">
            <SearchClearIcon searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input type="text" className="stui-search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search label, key, slug, target or permission..." />
          </div>
          <button className="stui-crud-btn" onClick={startAdd} title={selected ? `Add a row beside or under '${selected.label}'` : 'Add a top-level row'}>
            <Plus size={18} />
          </button>
          <button className={`stui-crud-btn${editMode ? ' active' : ''}`} onClick={() => setEditMode(m => !m)}
            title={editMode ? 'Leave edit mode' : 'Edit mode: double-click a row to edit it'}>
            {editMode ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className="stui-crud-btn" onClick={() => setShowHidden(v => !v)} title={showHidden ? 'Hide hidden rows' : 'Show hidden rows'}>
            {showHidden ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setExpanded(new Set(rows.map(r => r.navKey))); }}>
                  <ChevronsDown size={16} /><span>Expand all</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setExpanded(new Set()); }}>
                  <ChevronsUp size={16} /><span>Collapse all</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-datasheet-view">
        <div className="stui-data-grid-container">
          <div className="stui-data-grid-scroll-area">
            <table className="stui-data-grid">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Label</th><th style={{ width: '14%' }}>Type</th><th style={{ width: '18%' }}>Target</th>
                  <th style={{ width: '20%' }}>Key</th><th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={5} className="stui-td-empty">{searchTerm ? 'No matches.' : 'No rows.'}</td></tr>}
                {visible.map(({ row: r, depth, hasKids }) => {
                  const Icon = iconFor(r.icon);
                  const open = matchKeys || expanded.has(r.navKey);
                  return (
                    <tr key={r.navKey} data-nav-key={r.navKey}
                      className={['stui-clickable', selectedKey === r.navKey && 'stui-row-selected', r.isHidden && 'stui-inactive'].filter(Boolean).join(' ')}
                      onClick={() => setSelectedKey(r.navKey)} onDoubleClick={() => { setSelectedKey(r.navKey); openRow(r, !editMode); }}>
                      <td>
                        <span className="stui-tree-indent" style={{ '--tree-depth': depth }}>
                          <button className="stui-tree-toggle" disabled={!hasKids || !!matchKeys}
                            onClick={e => { e.stopPropagation(); toggle(r.navKey); }} title={open ? 'Collapse' : 'Expand'}>
                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                          {Icon && <Icon size={14} />}
                          <span>{r.label}</span>
                        </span>
                      </td>
                      <td>{r.navType}{r.surfaceType ? ` · ${r.surfaceType}` : ''}</td>
                      <td className="stui-truncate" title={r.targetKey || r.slug || undefined}><code>{r.targetKey || r.slug || ''}</code></td>
                      <td className="stui-truncate" title={r.permissionKey || undefined}>{r.permissionKey ? <code>{r.permissionKey}</code> : ''}</td>
                      <td>
                        {r.sysAdminOnly && <span className="stui-badge stui-badge-red stui-badge-xs">SysAdmin</span>}{' '}
                        {r.isFuture && <span className="stui-badge stui-badge-amber stui-badge-xs">Future</span>}{' '}
                        {r.isHidden && <span className="stui-badge stui-badge-gray stui-badge-xs">Hidden</span>}{' '}
                        {r.allowBulkDelete && <span className="stui-badge stui-badge-blue stui-badge-xs">Bulk delete</span>}{' '}
                        {!r.searchable && <span className="stui-badge stui-badge-gray stui-badge-xs">Not searched</span>}{' '}
                        {r.codeUse && <span className="stui-badge stui-badge-purple stui-badge-xs" title={r.codeUse}>Code</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>{editing.isNew ? 'New Navigation Row' : `${editing.readOnly ? 'View' : 'Edit'} — ${original?.label || editing.row.label}`}</h3>
              <button className="stui-modal-close" onClick={cancel}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid">
                <div className="stui-field-group stui-col-8">
                  <label>Type <span className="stui-required">*</span></label>
                  <StuiSelect className="stui-field-input" value={editing.row.navType} disabled={ro} onChange={e => setField('navType', e.target.value)}>
                    {(targets?.navTypes || []).map(t => <option key={t} value={t}>{t}</option>)}
                  </StuiSelect>
                  <p className="stui-hint">A group, a table page, a data entry module, a report, or a page the code supplies.</p>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>NavKey <span className="stui-required">*</span></label>
                  <input className="stui-field-input" value={editing.row.navKey} disabled={!editing.isNew || ro} autoFocus={editing.isNew}
                    onChange={e => setField('navKey', e.target.value)} placeholder="e.g. pwr-dist" />
                  <p className="stui-hint">The row's permanent id: links and every save from the page name it. Never changes.</p>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Label <span className="stui-required">*</span></label>
                  <input className="stui-field-input" value={editing.row.label} disabled={ro} onChange={e => setField('label', e.target.value)} />
                  <p className="stui-hint">What the sidebar shows.</p>
                </div>
                <div className="stui-field-group stui-col-16">
                  <label>Parent</label>
                  <SearchSelect className="stui-field-input" value={editing.row.parentKey} options={parentOptions}
                    displayValue={shown(parentOptions, editing.row.parentKey)} disabled={ro}
                    placeholder="-- Top level --" onChange={v => setField('parentKey', v || '')} />
                  <p className="stui-hint">The group it sits under.{editing.row.navType === 'Report' ? ' A report sits under a reports section and takes its key.' : ''}</p>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Sort Order</label>
                  <input className="stui-field-input" type="number" value={editing.row.sortOrder} disabled={ro} onChange={e => setField('sortOrder', e.target.value)} />
                  <p className="stui-hint">Position among its siblings, lowest first.</p>
                </div>
                {uses(editing.row.navType, 'targetKey') && (
                  <div className="stui-field-group stui-col-12">
                    <label>Target</label>
                    {targetField()}
                    <p className="stui-hint">{TARGET_HINT[editing.row.navType]}</p>
                  </div>
                )}
                {uses(editing.row.navType, 'slug') && (
                  <div className="stui-field-group stui-col-12">
                    <label>Slug (URL path)</label>
                    <input className="stui-field-input" value={editing.row.slug} disabled={ro} onChange={e => setField('slug', e.target.value)} placeholder="e.g. pwr-dist" />
                    <p className="stui-hint">{editing.row.navType === 'Page' ? 'The address it opens; it must match a route in the app.'
                      : editing.row.navType === 'Header' ? 'Optional: a landing page for the group.' : 'The address after the database, e.g. /gf/pwr-dist.'}</p>
                  </div>
                )}
                {uses(editing.row.navType, 'permissionKey') && (
                  <div className="stui-field-group stui-col-12">
                    <label>Permission Key</label>
                    <SearchSelect className="stui-field-input" value={editing.row.permissionKey} disabled={ro}
                      options={permissionOptions} displayValue={shown(permissionOptions, editing.row.permissionKey)}
                      placeholder="-- No key of its own --" onChange={v => setField('permissionKey', v || '')} />
                    <p className="stui-hint">{KEY_HINT[editing.row.navType] || KEY_HINT.Page}</p>
                  </div>
                )}
                {uses(editing.row.navType, 'surfaceType') && (
                  <div className="stui-field-group stui-col-12">
                    <label>Write Surface</label>
                    <StuiSelect className="stui-field-input" value={editing.row.surfaceType} disabled={ro} onChange={e => setField('surfaceType', e.target.value)}>
                      <option value="">From the type</option>
                      {(targets?.surfaceTypes || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </StuiSelect>
                    <p className="stui-hint">How saves from the page are judged. Leave it on From the type unless the page is Bulk Import or a transmittal log.</p>
                  </div>
                )}
                {uses(editing.row.navType, 'icon') && (
                  <div className="stui-field-group stui-col-12">
                    <label>Icon {PickedIcon && <PickedIcon size={14} />}</label>
                    <SearchSelect className="stui-field-input" value={editing.row.icon} options={ICON_NAMES} isLookup disabled={ro}
                      placeholder="-- No icon --" onChange={v => setField('icon', v || '')} />
                    <p className="stui-hint">Shown on top-level rows.</p>
                  </div>
                )}
                <div className="stui-field-group stui-col-12">
                  <label>Flags</label>
                  <div className="stui-checkbox-list">
                    {FLAGS.filter(([k]) => k !== 'allowBulkDelete' || ['Engineering', 'Page'].includes(editing.row.navType)).map(([k, label]) => (
                      <label key={k} className="stui-checkbox">
                        <input type="checkbox" className="stui-checkbox-input" checked={!!editing.row[k]} disabled={ro} onChange={e => setField(k, e.target.checked)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  <p className="stui-hint">SysAdmin only and Future keep it from everyone else. Hidden removes it for everyone. Searchable puts it in the sidebar search. Bulk delete lets the list delete several rows at once.</p>
                </div>
              </div>
              {consequences && (
                <div className="stui-alert stui-alert-warning">
                  <strong>This change reaches past the row:</strong>
                  <ul>{consequences.map(c => <li key={c}>{c}</li>)}</ul>
                </div>
              )}
              <p className="stui-hint">
                Rows are hidden, never deleted. Who can open and save from each row: <Link to="/admin/permissions?tab=matrix">Security &gt; Permissions &gt; Matrix</Link>.
              </p>
            </div>
            <div className="stui-modal-footer">
              {editing.readOnly ? (
                <button className="stui-btn stui-btn-primary" onClick={cancel}>Close</button>
              ) : <>
              <button className="stui-btn stui-btn-secondary" onClick={cancel}>Cancel</button>
              {consequences ? (
                <button className="stui-btn stui-btn-danger" onClick={() => save(true)} disabled={saving}>Save anyway</button>
              ) : (
                <button className="stui-btn stui-btn-primary" onClick={() => save(false)} disabled={!dirty || saving}><Save size={14} /> Save</button>
              )}
              </>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationEditorPage;
